import React, { useState, useEffect, useMemo } from 'react';
import { FormikHelpers, FormikValues } from 'formik';
import {
  Grid,
  Stack,
  Checkbox,
  FormControl,
  FormGroup,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Skeleton,
} from '@mui/material';
import { ErrorOutline } from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import {
  Button,
  InfoCard,
  StylovyzeComposableDialog,
  StylovyzeFormV2,
  Text,
  useUnits,
} from '@innovyze/stylovyze';
import { useGlobalization } from '@Translations';
import { getLengthCostPerDisplayLabel } from '@Utils/lengthUnitCost';
import { getGroupSet } from '@Services/groupSets.services';
import { normalizeGroupSetAssetType } from '@Utils/groupSetUtils';
import { GroupSet } from '@Types';
import { useAssetTypeSummary, useSystemType } from '@Hooks/assetSchemaHooks';
import { useHasEditAccess } from '@Hooks/useHasEditAccess';

const StyledGridItem = styled(Grid)(({ theme }) => ({
  borderBottom: `1px solid ${theme.palette.divider}`,
  paddingBottom: theme.spacing(1.5),
  paddingTop: theme.spacing(1.5),
}));

interface SingleCostTableConfigurationData {
  id: string;
  name: string;
  assetGroupSet: string;
  costPer: string;
  lastUpdated: string;
}

interface SingleCostTableConfigurationProps {
  onUpdate?: (unit: string) => void;
  loading?: boolean;
  data: SingleCostTableConfigurationData;
}

const SingleCostTableConfiguration: React.FC<
  SingleCostTableConfigurationProps
> = ({ onUpdate, loading = false, data }) => {
  const { t } = useGlobalization();
  const { system } = useUnits();
  const hasEditAccess = useHasEditAccess();
  const [isOpen, setOpen] = useState(false);
  const [confirmChange, setConfirmChange] = useState(false);
  const [groupSet, setGroupSet] = useState<{
    isLoading: boolean;
    error: string | null;
    groupSet: GroupSet | null;
  }>({
    isLoading: true,
    error: null,
    groupSet: null,
  });

  useEffect(() => {
    const fetchGroupSet = async () => {
      if (!data.assetGroupSet) {
        setGroupSet({ isLoading: false, error: null, groupSet: null });
        return;
      }

      try {
        const response = await getGroupSet(data.assetGroupSet);
        const fetchedGroupSet = response.data.data;
        setGroupSet({
          isLoading: false,
          error: null,
          groupSet: {
            ...fetchedGroupSet,
            assetType: normalizeGroupSetAssetType(fetchedGroupSet),
          },
        });
      } catch (error) {
        console.error('Error fetching group set:', error);
        setGroupSet({
          isLoading: false,
          error: 'Failed to load',
          groupSet: null,
        });
      }
    };

    fetchGroupSet();
  }, [data.assetGroupSet]);

  const assetTypeSummary = useAssetTypeSummary(
    groupSet.groupSet?.assetType ?? ''
  );
  const systemType = useSystemType(assetTypeSummary.data?.systemTypeId ?? '');
  const systemAndAssetType =
    assetTypeSummary.data && systemType.data
      ? t('{{systemType}} - {{assetType}}', {
          systemType: t(systemType.data.displayName),
          assetType: t(assetTypeSummary.data.displayName),
        })
      : '';

  const unitOptions = useMemo(
    () => [
      { key: 'each', value: 'each' },
      {
        key: 'length',
        value: getLengthCostPerDisplayLabel(t, system),
      },
    ],
    [t, system]
  );

  const getUnitDisplayValue = (unit: string) => {
    const option = unitOptions.find(opt => opt.key === unit);
    return option ? option.value : unit;
  };

  const handleUpdate = async (
    values: FormikValues,
    actions: FormikHelpers<FormikValues>
  ) => {
    if (!confirmChange) {
      return;
    }

    const selectedUnit = values.unit;
    try {
      if (onUpdate) {
        onUpdate(selectedUnit);
      }
      setOpen(false);
      setConfirmChange(false);
    } catch (err) {
      console.error('Error updating unit:', err);
    } finally {
      actions.setSubmitting(false);
    }
  };

  const handleCancel = () => {
    setOpen(false);
    setConfirmChange(false);
  };

  return (
    <>
      <StylovyzeFormV2
        initialValues={{
          unit: data.costPer,
        }}
        onSubmit={handleUpdate}
        enableReinitialize>
        {({ submitForm, isSubmitting, errors, values, setFieldValue }) => {
          const hasValueChanged = values.unit !== data.costPer;

          return (
            <StylovyzeComposableDialog
              onCancel={handleCancel}
              open={isOpen}
              title={t('Edit Cost Table Configuration')}
              footer={
                <>
                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={handleCancel}>
                    {t('Cancel')}
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    disabled={
                      isSubmitting || !confirmChange || !hasValueChanged
                    }
                    onClick={submitForm}>
                    {t('Update')}
                  </Button>
                </>
              }>
              <FormControl
                fullWidth
                variant="outlined"
                margin="normal"
                error={!!errors.unit}>
                <InputLabel>{t('Cost per')}</InputLabel>
                <Select
                  name="unit"
                  value={values.unit}
                  onChange={e => setFieldValue('unit', e.target.value)}
                  label={t('Cost per')}>
                  {unitOptions.map(opt => (
                    <MenuItem key={opt.key} value={opt.key}>
                      {opt.value}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Stack spacing={2} direction="column" sx={{ mt: 2 }}>
                <Stack direction="row" spacing={1} alignItems="flex-start">
                  <ErrorOutline sx={{ mt: '2px' }} />
                  <Text>
                    {t(
                      'Changing the unit will affect all cost calculations in this table.'
                    )}
                  </Text>
                </Stack>
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={confirmChange}
                        onChange={e => setConfirmChange(e.target.checked)}
                        size="small"
                        color="default"
                      />
                    }
                    label={<Text>{t('I understand and want to proceed')}</Text>}
                  />
                </FormGroup>
              </Stack>
            </StylovyzeComposableDialog>
          );
        }}
      </StylovyzeFormV2>

      <InfoCard
        title={t('Configuration')}
        menuItems={
          loading || !hasEditAccess
            ? []
            : [
                {
                  text: t('Edit'),
                  onClick: () => setOpen(true),
                },
              ]
        }
        buttonStyle={{
          variant: 'outlined',
          color: 'primary',
        }}>
        {loading ? (
          <Stack spacing={2}>
            <Skeleton variant="text" width="100%" height={40} />
            <Skeleton variant="text" width="100%" height={40} />
            <Skeleton variant="text" width="100%" height={40} />
          </Stack>
        ) : (
          <Grid container spacing={2}>
            <StyledGridItem item xs={2}>
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <Text variant="body2" color="text.secondary">
                  {t('Asset Group Set')}
                </Text>
              </Stack>
            </StyledGridItem>
            <StyledGridItem item xs={10}>
              {groupSet.isLoading ? (
                <Skeleton variant="text" width={200} height={24} />
              ) : (
                <Text variant="body2">
                  {groupSet.groupSet?.name || data.assetGroupSet}
                </Text>
              )}
            </StyledGridItem>

            <StyledGridItem item xs={2}>
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <Text variant="body2" color="text.secondary">
                  {t('System and Asset Type')}
                </Text>
              </Stack>
            </StyledGridItem>
            <StyledGridItem item xs={10}>
              {groupSet.isLoading ? (
                <Skeleton variant="text" width={200} height={24} />
              ) : (
                <Text variant="body2">{systemAndAssetType}</Text>
              )}
            </StyledGridItem>

            <StyledGridItem item xs={2} sx={{ borderBottom: 'none' }}>
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <Text variant="body2" color="text.secondary">
                  {t('Cost per')}
                </Text>
              </Stack>
            </StyledGridItem>
            <StyledGridItem item xs={10} sx={{ borderBottom: 'none' }}>
              <Text variant="body2">{getUnitDisplayValue(data.costPer)}</Text>
            </StyledGridItem>
          </Grid>
        )}
      </InfoCard>
    </>
  );
};

export default SingleCostTableConfiguration;
